/**
 * Tab navigation layout with Y2K Glassmorphism
 *
 * Bottom tab navigation with 4 screens:
 * - Home (index) - Unified analysis with modern animation (formerly analyze-unified.tsx)
 * - History - Past analyses
 * - Stats - Analytics
 * - Settings - User preferences
 *
 * Features:
 * - GlassTabIcon with per-tab gradients (Mint→Teal, Lavender→Pink, Orange→Red, Gray)
 * - Frosted glass tab bar background (BlurView intensity: 60)
 * - Spring animations on selection (1.0 → 1.1 → 1.0 scale)
 * - Reduced motion support via AccessibilityInfo
 * - WCAG AA: 5.2:1 contrast for white icons
 *
 * Platform-specific:
 * - Web: Footer navigation hidden (DD-261) - navigation via header menu
 * - Mobile: Footer navigation visible with glass tab bar
 *
 * Note: Subscription screen is accessible via Settings > Manage Subscription
 * Architecture: index-classic.tsx removed in #833 (confirmed dead code)
 */
import { Tabs } from "expo-router";
import { Platform } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import { Ionicons } from "@expo/vector-icons";
import { GlassTabIcon } from "../../components/glass/GlassTabIcon";
import { GlassTabBarBackground } from "../../components/glass/GlassTabBarBackground";

// Y2K gradient colors (60% opacity for accessibility - 5.2:1 contrast)
const TAB_GRADIENTS = {
  home: ['rgba(16, 185, 129, 0.6)', 'rgba(6, 182, 212, 0.6)'] as const, // Mint → Teal
  history: ['rgba(192, 132, 252, 0.6)', 'rgba(236, 72, 153, 0.6)'] as const, // Lavender → Pink
  stats: ['rgba(249, 115, 22, 0.6)', 'rgba(239, 68, 68, 0.6)'] as const, // Orange → Red
  settings: ['rgba(156, 163, 175, 0.6)', 'rgba(107, 114, 128, 0.6)'] as const, // Gray
} as const;

const GLOW_COLORS = {
  home: '#06b6d4', // Teal glow
  history: '#ec4899', // Pink glow
  stats: '#ef4444', // Red glow
  settings: '#6b7280', // Gray glow
} as const;

export default function TabLayout() {
  const { t } = useTranslation('home');

  return (
    <Tabs
      screenOptions={{
        tabBarActiveTintColor: "#FFFFFF", // White for active text (WCAG AA on glass)
        tabBarInactiveTintColor: "#9CA3AF", // Gray-400 for inactive text
        tabBarStyle: Platform.OS === 'web'
          ? { display: 'none' } // DD-261: Hide footer navigation on web (navigation in header menu)
          : {
              borderTopWidth: 0, // Remove default border (glass provides visual separation)
              paddingBottom: 5,
              paddingTop: 5,
              height: 60,
              position: 'absolute', // Required for blur background
              backgroundColor: 'transparent', // Let GlassTabBarBackground show through
            },
        tabBarBackground: () => <GlassTabBarBackground />,
        headerShown: false,
        // Instant transition to eliminate white flash visibility window
        animation: 'none',
      }}
    >
      {/* Unified Analyze Screen - DEFAULT HOME (formerly analyze-unified.tsx, renamed to index.tsx) */}
      <Tabs.Screen
        name="index"
        options={{
          title: t("tabs.home"),
          tabBarIcon: ({ focused }) => (
            <GlassTabIcon
              gradient={TAB_GRADIENTS.home}
              glowColor={GLOW_COLORS.home}
              isActive={focused}
              icon={
                <Ionicons
                  name="home"
                  size={24}
                  color={focused ? "#FFFFFF" : "#9CA3AF"}
                />
              }
            />
          ),
        }}
      />
      <Tabs.Screen
        name="history"
        options={{
          title: t("tabs.history"),
          tabBarIcon: ({ focused }) => (
            <GlassTabIcon
              gradient={TAB_GRADIENTS.history}
              glowColor={GLOW_COLORS.history}
              isActive={focused}
              icon={
                <Ionicons
                  name="time"
                  size={24}
                  color={focused ? "#FFFFFF" : "#9CA3AF"}
                />
              }
            />
          ),
        }}
      />
      <Tabs.Screen
        name="stats"
        options={{
          title: t("tabs.stats"),
          tabBarIcon: ({ focused }) => (
            <GlassTabIcon
              gradient={TAB_GRADIENTS.stats}
              glowColor={GLOW_COLORS.stats}
              isActive={focused}
              icon={
                <Ionicons
                  name="stats-chart"
                  size={24}
                  color={focused ? "#FFFFFF" : "#9CA3AF"}
                />
              }
            />
          ),
        }}
      />
      <Tabs.Screen
        name="subscription"
        options={{
          href: null, // Hide from tab bar, accessible via Settings
        }}
      />
      <Tabs.Screen
        name="settings"
        options={{
          title: t("tabs.settings"),
          tabBarIcon: ({ focused }) => (
            <GlassTabIcon
              gradient={TAB_GRADIENTS.settings}
              glowColor={GLOW_COLORS.settings}
              isActive={focused}
              icon={
                <Ionicons
                  name="settings"
                  size={24}
                  color={focused ? "#FFFFFF" : "#9CA3AF"}
                />
              }
            />
          ),
        }}
      />
      {/* Family Plan - Hidden from tab bar, accessible via Settings or deep link */}
      <Tabs.Screen
        name="family-manage"
        options={{
          href: null, // Hide from tab bar (accessible when user is family member)
        }}
      />
      {/* Practitioner Groups - Hidden from tab bar, accessible via Settings or deep link */}
      <Tabs.Screen
        name="practitioner-manage"
        options={{
          href: null, // Hide from tab bar (Phase 6 - accessible when user has practitioner groups)
        }}
      />
      {/* Dynamic Routes - Hidden from tab bar, accessible via programmatic navigation */}
      <Tabs.Screen
        name="analyze/[id]"
        options={{
          href: null, // Hide from tab bar (animation phase route)
        }}
      />
      <Tabs.Screen
        name="results/[analysisId]"
        options={{
          href: null, // Hide from tab bar (results route with shareable links)
        }}
      />
    </Tabs>
  );
}
